import React, { useEffect, useState } from 'react';
import { kurumAPI } from '../api';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Building2, X, Check } from 'lucide-react';

const PRESET_COLORS = [
  '#1E3A5F','#304F9D','#D4AF37','#0F6E56','#993C1D',
  '#993556','#3B6D11','#854F0B','#185FA5','#6B21A8',
];

function KurumModal({ kurum, onClose, onSaved }) {
  const isEdit = !!kurum?.id;
  const [ad, setAd] = useState(kurum?.ad || '');
  const [renk, setRenk] = useState(kurum?.renk || PRESET_COLORS[0]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ad.trim()) { toast.error('Kurum adı zorunlu'); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await kurumAPI.update(kurum.id, { ad, renk });
        toast.success('Kurum güncellendi');
      } else {
        await kurumAPI.create({ ad, renk });
        toast.success('Kurum eklendi');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop bg-navy-900/40">
      <div className="modal-content bg-white w-full max-w-md rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy-100">
          <h2 className="font-display text-lg text-navy-800">
            {isEdit ? 'Kurumu Düzenle' : 'Yeni Kurum'}
          </h2>
          <button onClick={onClose} className="btn-ghost p-1.5"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
          <div>
            <label className="label">Kurum Adı <span className="text-rose-500">*</span></label>
            <input
              className="input"
              placeholder="Kurum adını girin"
              value={ad}
              onChange={(e) => setAd(e.target.value)}
              autoFocus
              required
            />
          </div>

          <div>
            <label className="label">Renk</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setRenk(c)}
                  className="w-8 h-8 rounded-lg border-2 transition-all flex items-center justify-center"
                  style={{
                    backgroundColor: c,
                    borderColor: renk === c ? '#D4AF37' : 'transparent',
                    transform: renk === c ? 'scale(1.15)' : 'scale(1)',
                  }}
                >
                  {renk === c && <Check size={14} className="text-white" />}
                </button>
              ))}
              <input
                type="color"
                value={renk}
                onChange={(e) => setRenk(e.target.value)}
                className="w-8 h-8 rounded-lg cursor-pointer border border-navy-200"
                title="Özel renk seç"
              />
            </div>
            {/* Önizleme */}
            <div className="mt-3 flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-semibold"
                style={{ backgroundColor: renk }}
              >
                {ad?.[0]?.toUpperCase() || 'K'}
              </div>
              <span className="text-sm text-navy-600 font-medium">{ad || 'Kurum Adı'}</span>
            </div>
          </div>
        </form>

        <div className="flex gap-2 justify-end px-5 py-4 border-t border-navy-100">
          <button type="button" onClick={onClose} className="btn-secondary">İptal</button>
          <button onClick={handleSubmit} className="btn-primary" disabled={loading}>
            {loading && <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            {isEdit ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function KurumlarPage() {
  const [kurumlar, setKurumlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editKurum, setEditKurum] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = () => {
    setLoading(true);
    kurumAPI.list()
      .then((r) => setKurumlar(r.data))
      .catch(() => toast.error('Kurumlar yüklenemedi'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    try {
      await kurumAPI.delete(id);
      toast.success('Kurum silindi');
      setDeleteConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  return (
    <div className="max-w-3xl space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display text-navy-800">Kurumlar</h1>
          <p className="text-navy-400 text-sm mt-0.5">{kurumlar.length} kurum kayıtlı</p>
        </div>
        <button className="btn-primary" onClick={() => { setEditKurum(null); setShowModal(true); }}>
          <Plus size={16} /> Yeni Kurum
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-7 h-7 border-2 border-navy-200 border-t-navy-600 rounded-full animate-spin" />
        </div>
      ) : kurumlar.length === 0 ? (
        <div className="card p-12 text-center">
          <Building2 size={40} className="mx-auto text-navy-200 mb-3" />
          <p className="text-navy-400 text-sm">Henüz kurum eklenmemiş</p>
          <button className="btn-primary mt-4 mx-auto" onClick={() => setShowModal(true)}>
            <Plus size={16} /> İlk Kurumu Ekle
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {kurumlar.map((k) => (
            <div key={k.id} className="card p-5 flex items-center gap-4 hover:shadow-card-hover transition-shadow">
              {/* Renk avatarı */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0 font-display"
                style={{ backgroundColor: k.renk }}
              >
                {k.ad[0]}
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy-800 truncate">{k.ad}</p>
                <p className="text-navy-400 text-xs mt-0.5">
                  {k.firma_sayisi?.toLocaleString('tr-TR') || 0} firma bağlı
                </p>
                {/* Progress bar */}
                <div className="mt-2 h-1 bg-navy-100 rounded-full overflow-hidden w-24">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      backgroundColor: k.renk,
                      width: `${Math.min(100, (k.firma_sayisi || 0) / Math.max(...kurumlar.map(x => x.firma_sayisi || 1)) * 100)}%`
                    }}
                  />
                </div>
              </div>

              <div className="flex gap-1 flex-shrink-0">
                <button
                  className="btn-ghost p-1.5"
                  onClick={() => { setEditKurum(k); setShowModal(true); }}
                >
                  <Edit2 size={15} />
                </button>
                <button
                  className="btn-ghost p-1.5 hover:text-red-500"
                  onClick={() => setDeleteConfirm(k)}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <KurumModal
          kurum={editKurum}
          onClose={() => { setShowModal(false); setEditKurum(null); }}
          onSaved={() => { setShowModal(false); setEditKurum(null); load(); }}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop bg-navy-900/40">
          <div className="modal-content bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-display text-lg text-navy-800 mb-2">Kurumu Sil</h3>
            <p className="text-navy-500 text-sm mb-1">
              <strong className="text-navy-800">{deleteConfirm.ad}</strong> silinecek.
            </p>
            {deleteConfirm.firma_sayisi > 0 && (
              <p className="text-amber-600 text-xs bg-amber-50 rounded-lg p-2 mb-3">
                Bu kuruma bağlı {deleteConfirm.firma_sayisi} firma var. Silmeden önce firmaları başka kuruma taşıyın.
              </p>
            )}
            <div className="flex gap-2 justify-end mt-4">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Vazgeç</button>
              <button className="btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
